import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Shield, AlertTriangle, Sparkles, MessageSquare, FileText,
  Mic, CreditCard, AlertOctagon, Loader2, ChevronRight, Mail, Calendar,
  MapPin, User as UserIcon, Briefcase, Power, KeyRound, Coins, Download,
  Trash2, LogOut, Activity, Database, RefreshCw,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../components/ui/dialog";
import { Textarea } from "../components/ui/textarea";

// ===========================================================================
// Helpers
// ===========================================================================
function StatusBadge({ user }) {
  if (user.active === false) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-red-600 bg-red-50 px-2 py-1 rounded-sm border border-red-200" data-testid="user-status-badge">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Disabled
      </span>
    );
  }
  if (user.email_verified === false) {
    return (
      <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-amber-600 bg-amber-50 px-2 py-1 rounded-sm border border-amber-200" data-testid="user-status-badge">
        <span className="h-1.5 w-1.5 rounded-full bg-amber-500" /> Unverified
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-mono uppercase tracking-wider text-emerald-700 bg-emerald-50 px-2 py-1 rounded-sm border border-emerald-200" data-testid="user-status-badge">
      <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" /> Active
    </span>
  );
}

function KpiTile({ label, value, hint, testid }) {
  return (
    <div className="border border-border bg-card p-3 rounded-sm" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-wider font-mono text-muted-foreground">{label}</div>
      <div className="text-xl font-display font-bold mt-0.5">{value}</div>
      {hint && <div className="text-[10px] font-mono text-muted-foreground mt-0.5">{hint}</div>}
    </div>
  );
}

function fmtBytes(b) {
  if (!b) return "0 B";
  const k = 1024;
  if (b < k) return `${b} B`;
  if (b < k * k) return `${(b / k).toFixed(1)} KB`;
  if (b < k * k * k) return `${(b / (k * k)).toFixed(1)} MB`;
  return `${(b / (k * k * k)).toFixed(2)} GB`;
}

// ===========================================================================
// Main page
// ===========================================================================
export default function AdminUserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/overview`);
      setOverview(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load user");
      navigate("/admin");
    } finally {
      setLoading(false);
    }
  }, [userId, navigate]);

  useEffect(() => { refresh(); }, [refresh]);

  if (loading || !overview) {
    return (
      <div className="min-h-screen grid place-items-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const u = overview.user;
  const cp = overview.context_profile || {};
  const kpis = overview.kpis || {};

  return (
    <div className="min-h-screen bg-background">
      {/* Top bar */}
      <div className="border-b border-border bg-card sticky top-0 z-30">
        <div className="max-w-6xl mx-auto px-6 py-3 flex items-center gap-3">
          <Link to="/admin" className="inline-flex items-center gap-1.5 text-xs font-mono text-muted-foreground hover:text-foreground" data-testid="back-to-admin">
            <ArrowLeft className="h-3.5 w-3.5" /> Volver a Usuarios
          </Link>
          <div className="flex-1" />
          <div className="hidden md:flex items-center gap-1.5 text-[11px] font-mono text-amber-600">
            <Shield className="h-3.5 w-3.5" /> Toda lectura queda registrada
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {/* Header card */}
        <div className="border border-border bg-card p-5 rounded-sm">
          <div className="flex flex-wrap items-start gap-4">
            <div className="h-16 w-16 rounded-sm bg-secondary grid place-items-center font-display font-black text-2xl">
              {(u.name || u.email).slice(0, 2).toUpperCase()}
            </div>
            <div className="flex-1 min-w-[240px]">
              <h1 className="font-display font-black text-2xl tracking-tight" data-testid="user-detail-name">{u.name || "—"}</h1>
              <div className="font-mono text-sm text-muted-foreground" data-testid="user-detail-email">{u.email}</div>
              <div className="flex flex-wrap items-center gap-2 mt-2">
                <StatusBadge user={u} />
                <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground bg-secondary px-1.5 py-0.5 rounded-sm">{u.role}</span>
                {cp.country && <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground"><MapPin className="inline h-3 w-3" /> {cp.country}</span>}
                {overview.age_band && <span className="text-[11px] font-mono text-muted-foreground"><Calendar className="inline h-3 w-3" /> {overview.age_band}</span>}
                {cp.profile_type && <span className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground"><Briefcase className="inline h-3 w-3" /> {cp.profile_type}</span>}
                {cp.company && <span className="text-[11px] font-mono text-muted-foreground">@ {cp.company}</span>}
              </div>
            </div>
          </div>

          {/* KPI grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-5">
            <KpiTile label="Chats"     value={kpis.conversations}      testid="kpi-conversations" />
            <KpiTile label="Mensajes"  value={kpis.messages}            testid="kpi-messages" />
            <KpiTile label="Archivos"  value={kpis.files}               testid="kpi-files" />
            <KpiTile label="Storage"   value={fmtBytes(kpis.storage_bytes)} testid="kpi-storage" />
            <KpiTile label="Voz"       value={kpis.voice_recordings}   testid="kpi-recordings" />
            <KpiTile label="Saldo"     value={(kpis.wallet_balance_tokens || 0).toLocaleString()} hint="tokens" testid="kpi-wallet" />
            <KpiTile label="Gasto"     value={`$${(kpis.lifetime_spend_usd || 0).toFixed(2)}`} hint="vida" testid="kpi-spend" />
            <KpiTile label="Comprado"  value={(kpis.lifetime_purchased_tokens || 0).toLocaleString()} hint="tokens vida" testid="kpi-purchased" />
          </div>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="overview" className="space-y-4">
          <TabsList className="bg-secondary/40 rounded-sm h-auto flex-wrap" data-testid="user-detail-tabs">
            <TabsTrigger value="overview"    className="rounded-sm" data-testid="tab-overview"><Activity className="h-3.5 w-3.5 mr-1.5" />Overview</TabsTrigger>
            <TabsTrigger value="onboarding"  className="rounded-sm" data-testid="tab-onboarding"><Sparkles className="h-3.5 w-3.5 mr-1.5" />Onboarding</TabsTrigger>
            <TabsTrigger value="chats"       className="rounded-sm" data-testid="tab-chats"><MessageSquare className="h-3.5 w-3.5 mr-1.5" />Chats</TabsTrigger>
            <TabsTrigger value="files"       className="rounded-sm" data-testid="tab-files"><FileText className="h-3.5 w-3.5 mr-1.5" />Archivos</TabsTrigger>
            <TabsTrigger value="voice"       className="rounded-sm" data-testid="tab-voice"><Mic className="h-3.5 w-3.5 mr-1.5" />Voz</TabsTrigger>
            <TabsTrigger value="billing"     className="rounded-sm" data-testid="tab-billing"><CreditCard className="h-3.5 w-3.5 mr-1.5" />Billing</TabsTrigger>
            <TabsTrigger value="danger"      className="rounded-sm text-red-600 data-[state=active]:bg-red-50" data-testid="tab-danger"><AlertOctagon className="h-3.5 w-3.5 mr-1.5" />Danger zone</TabsTrigger>
          </TabsList>

          <TabsContent value="overview"><OverviewTab overview={overview} /></TabsContent>
          <TabsContent value="onboarding"><OnboardingPanel userId={userId} initial={cp} onSaved={refresh} /></TabsContent>
          <TabsContent value="chats"><ChatsPanel userId={userId} /></TabsContent>
          <TabsContent value="files"><FilesPanel userId={userId} /></TabsContent>
          <TabsContent value="voice"><VoicePanel userId={userId} /></TabsContent>
          <TabsContent value="billing"><BillingPanel userId={userId} kpis={kpis} onChanged={refresh} /></TabsContent>
          <TabsContent value="danger"><DangerZonePanel user={u} overview={overview} onChanged={refresh} /></TabsContent>
        </Tabs>
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Overview — recent activity + session info
// ===========================================================================
function OverviewTab({ overview }) {
  const sess = overview.session || {};
  const deact = overview.deactivation || {};
  const recent = overview.recent_activity || [];
  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-border bg-card p-4 rounded-sm">
        <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3">Sesión</div>
        <dl className="space-y-1.5 text-sm">
          <Row k="Último login" v={sess.last_login_at ? new Date(sess.last_login_at).toLocaleString() : "—"} />
          <Row k="Login count" v={sess.login_count ?? 0} />
          <Row k="IP" v={sess.last_login_ip || "—"} mono />
          <Row k="User agent" v={sess.last_login_ua || "—"} mono small />
        </dl>
        {deact.active === false && (
          <div className="mt-4 p-3 border border-red-200 bg-red-50 rounded-sm text-xs">
            <div className="font-bold text-red-700 mb-1">Desactivado</div>
            <div className="text-red-600">{deact.reason || "Sin motivo"}</div>
            <div className="text-red-500 font-mono mt-1">{deact.deactivated_at ? new Date(deact.deactivated_at).toLocaleString() : ""}</div>
          </div>
        )}
      </div>
      <div className="border border-border bg-card p-4 rounded-sm">
        <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-3">Actividad reciente</div>
        <ul className="space-y-2 text-xs" data-testid="recent-activity">
          {recent.length === 0 && <li className="text-muted-foreground">Sin actividad registrada.</li>}
          {recent.map((ev, i) => (
            <li key={i} className="flex items-center justify-between font-mono">
              <span>{ev.feature}</span>
              <span className="text-muted-foreground">{ev.tokens ? `${ev.tokens} tk` : ""}</span>
              <span className="text-muted-foreground">{ev.ts ? new Date(ev.ts).toLocaleString() : ""}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function Row({ k, v, mono, small }) {
  return (
    <div className="flex justify-between gap-3">
      <dt className="text-muted-foreground">{k}</dt>
      <dd className={`text-right truncate ${mono ? "font-mono" : ""} ${small ? "text-xs" : ""}`}>{v}</dd>
    </div>
  );
}

// ===========================================================================
// Tab: Onboarding (read-only display + edit-as-admin)
// ===========================================================================
function OnboardingPanel({ userId, initial, onSaved }) {
  const [profile, setProfile] = useState(initial);
  const [editing, setEditing] = useState(false);

  const fields = [
    ["role_label", "Rol"], ["role_key", "Role key"], ["background", "Background"],
    ["tone", "Tono"], ["language", "Idioma IA"],
    ["date_of_birth", "Fecha de nacimiento"], ["country", "País"], ["pronouns", "Pronombres"],
    ["profile_type", "Tipo de perfil"], ["company", "Empresa"], ["industry", "Industria"],
    ["company_size", "Tamaño"], ["seniority", "Seniority"], ["work_email", "Email laboral"],
    ["primary_goals", "Objetivos"], ["ai_familiarity", "Familiaridad IA"],
    ["preferred_length", "Largo respuesta"], ["reading_level", "Nivel lectura"],
    ["reduce_motion", "Reducir animaciones"],
    ["onboarding_step", "Paso wizard"], ["onboarding_completed", "Onboarding completo"],
  ];

  const save = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/users/${userId}/onboarding`, patch);
      setProfile(data);
      onSaved?.();
      toast.success("Onboarding actualizado");
      setEditing(false);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    }
  };

  return (
    <div className="border border-border bg-card p-5 rounded-sm" data-testid="admin-onboarding-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="text-sm font-bold">Datos del onboarding</div>
        <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setEditing(true)} data-testid="edit-onboarding-button">
          Editar como admin
        </Button>
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
        {fields.map(([k, label]) => {
          let v = profile?.[k];
          if (Array.isArray(v)) v = v.join(", ");
          else if (v === true) v = "sí";
          else if (v === false) v = "no";
          else if (v == null || v === "") v = "—";
          return <Row key={k} k={label} v={String(v)} />;
        })}
      </dl>
      {editing && (
        <EditOnboardingDialog
          open={editing}
          onClose={() => setEditing(false)}
          initial={profile}
          onSave={save}
        />
      )}
    </div>
  );
}

function EditOnboardingDialog({ open, onClose, initial, onSave }) {
  const [roleLabel, setRoleLabel] = useState(initial?.role_label || "");
  const [background, setBackground] = useState(initial?.background || "");
  const [tone, setTone] = useState(initial?.tone || "professional");
  const [language, setLanguage] = useState(initial?.language || "en");
  const submit = () => {
    onSave({ role_label: roleLabel, background, tone, language });
  };
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Editar onboarding como admin</DialogTitle>
          <DialogDescription>Esta acción queda registrada en el audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input value={roleLabel} onChange={(e) => setRoleLabel(e.target.value)} placeholder="Rol" data-testid="edit-onb-role" />
          <Textarea value={background} onChange={(e) => setBackground(e.target.value)} rows={3} placeholder="Background" data-testid="edit-onb-background" />
          <div className="grid grid-cols-2 gap-3">
            <Input value={tone} onChange={(e) => setTone(e.target.value)} placeholder="Tone" data-testid="edit-onb-tone" />
            <Input value={language} onChange={(e) => setLanguage(e.target.value)} placeholder="Lang (ISO)" data-testid="edit-onb-language" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} data-testid="edit-onb-save">Guardar</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Tab: Chats
// ===========================================================================
function ChatsPanel({ userId }) {
  const [items, setItems] = useState([]);
  const [activeConv, setActiveConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMsgs, setLoadingMsgs] = useState(false);

  useEffect(() => {
    api.get(`/admin/users/${userId}/conversations`).then(({ data }) => setItems(data.items || []));
  }, [userId]);

  const openConv = async (conv) => {
    setActiveConv(conv);
    setLoadingMsgs(true);
    try {
      const { data } = await api.get(`/admin/users/${userId}/conversations/${conv.id}/messages`);
      setMessages(data.messages || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load chat");
      setMessages([]);
    } finally {
      setLoadingMsgs(false);
    }
  };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="chats-list">
        <div className="px-3 py-2 text-xs uppercase tracking-wider font-mono text-muted-foreground border-b border-border">
          Conversaciones ({items.length})
        </div>
        <ul className="max-h-[600px] overflow-y-auto divide-y divide-border">
          {items.map((c) => (
            <li key={c.id}>
              <button
                onClick={() => openConv(c)}
                className={`w-full text-left p-3 hover:bg-muted/40 ${activeConv?.id === c.id ? "bg-muted/40" : ""}`}
                data-testid={`chat-item-${c.id}`}
              >
                <div className="text-sm font-medium truncate">{c.title || "(sin título)"}</div>
                <div className="text-[11px] font-mono text-muted-foreground flex gap-3 mt-1">
                  <span>{c.message_count || 0} msgs</span>
                  <span>{c.tokens_charged || 0} tk</span>
                  <span>${(c.cost_usd || 0).toFixed(4)}</span>
                  <span className="ml-auto">{c.updated_at ? new Date(c.updated_at).toLocaleDateString() : ""}</span>
                </div>
              </button>
            </li>
          ))}
          {items.length === 0 && <li className="p-6 text-center text-muted-foreground text-sm">Sin chats.</li>}
        </ul>
      </div>
      <div className="border border-border bg-card rounded-sm overflow-hidden">
        <div className="px-3 py-2 text-xs uppercase tracking-wider font-mono text-muted-foreground border-b border-border">
          {activeConv ? activeConv.title || "Sin título" : "Seleccioná un chat"}
        </div>
        <div className="max-h-[600px] overflow-y-auto p-3 space-y-3" data-testid="chat-messages">
          {loadingMsgs && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
          {!loadingMsgs && messages.map((m) => (
            <div key={m.id} className="text-sm">
              <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">
                {m.role} · {m.created_at ? new Date(m.created_at).toLocaleString() : ""}
              </div>
              <div className="mt-0.5 whitespace-pre-wrap">{m.content}</div>
              {m.file_ids?.length > 0 && <div className="text-[11px] font-mono text-muted-foreground mt-1">📎 {m.file_ids.length} archivo(s)</div>}
            </div>
          ))}
          {!loadingMsgs && messages.length === 0 && activeConv && (
            <div className="text-center text-muted-foreground text-sm py-6">Chat vacío.</div>
          )}
        </div>
      </div>
    </div>
  );
}

// ===========================================================================
// Tab: Files
// ===========================================================================
function FilesPanel({ userId }) {
  const [items, setItems] = useState([]);
  const navigate = useNavigate();
  useEffect(() => {
    api.get(`/admin/users/${userId}/files`).then(({ data }) => setItems(data || []));
  }, [userId]);
  const grouped = items.reduce((acc, f) => {
    const k = f.kind || "other";
    if (!acc[k]) acc[k] = [];
    acc[k].push(f);
    return acc;
  }, {});
  if (items.length === 0) return <Empty msg="Sin archivos." />;
  return (
    <div className="space-y-5" data-testid="files-panel">
      {Object.entries(grouped).map(([kind, list]) => (
        <div key={kind}>
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">{kind} ({list.length})</div>
          <div className="border border-border bg-card rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-secondary/30 border-b border-border">
                <tr className="text-left text-[10px] uppercase tracking-wider font-mono text-muted-foreground">
                  <th className="p-3">Name</th>
                  <th className="p-3">Size</th>
                  <th className="p-3">Status</th>
                  <th className="p-3">Chunks</th>
                  <th className="p-3">Profile</th>
                  <th className="p-3">Date</th>
                </tr>
              </thead>
              <tbody>
                {list.map((f) => (
                  <tr
                    key={f.id}
                    className="border-b last:border-0 border-border hover:bg-muted/40 cursor-pointer"
                    onClick={() => navigate(`/admin/users/${userId}/files/${f.id}`)}
                    data-testid={`file-row-${f.id}`}
                  >
                    <td className="p-3 font-mono text-xs hover:underline">{f.name}</td>
                    <td className="p-3 text-xs text-muted-foreground">{fmtBytes(f.size_bytes)}</td>
                    <td className="p-3 text-xs text-muted-foreground">
                      <span className={`px-1.5 py-0.5 rounded-sm border text-[10px] uppercase ${f.status === "ready" ? "border-emerald-300 text-emerald-700 bg-emerald-50" : "border-border text-muted-foreground"}`}>{f.status}</span>
                    </td>
                    <td className="p-3 text-xs font-mono">{f.chunk_count ?? "—"}</td>
                    <td className="p-3 text-xs font-mono">
                      {f.profile ? (
                        <span className="px-1.5 py-0.5 rounded-sm border border-amber-300 bg-amber-50 dark:bg-amber-950/20 text-[10px] uppercase">{f.profile}</span>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-3 text-xs text-muted-foreground">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
}

// ===========================================================================
// Tab: Voice
// ===========================================================================
function VoicePanel({ userId }) {
  const [items, setItems] = useState([]);
  useEffect(() => {
    api.get(`/admin/users/${userId}/recordings`).then(({ data }) => setItems(data || []));
  }, [userId]);
  if (items.length === 0) return <Empty msg="Sin grabaciones de voz." />;
  return (
    <div className="border border-border bg-card rounded-sm overflow-hidden" data-testid="voice-panel">
      <table className="w-full text-sm">
        <thead className="bg-secondary/30 border-b border-border">
          <tr className="text-left text-xs uppercase tracking-wider font-mono text-muted-foreground">
            <th className="p-3">Fecha</th>
            <th className="p-3">Duración</th>
            <th className="p-3">Voces</th>
            <th className="p-3">Idioma</th>
            <th className="p-3">Status</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => (
            <tr key={r.id} className="border-b last:border-0 border-border hover:bg-muted/40">
              <td className="p-3 text-xs font-mono">{r.created_at ? new Date(r.created_at).toLocaleString() : ""}</td>
              <td className="p-3 text-xs">{r.duration_seconds ? `${r.duration_seconds}s` : "—"}</td>
              <td className="p-3 text-xs">{r.num_speakers || "—"}</td>
              <td className="p-3 text-xs font-mono">{r.language || "—"}</td>
              <td className="p-3 text-xs font-mono">{r.status}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ===========================================================================
// Tab: Billing
// ===========================================================================
function BillingPanel({ userId, kpis, onChanged }) {
  const [ledger, setLedger] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [usage, setUsage] = useState({ by_feature: [] });
  const [grantOpen, setGrantOpen] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/admin/users/${userId}/ledger`).then(({ data }) => setLedger(data || []));
    api.get(`/admin/users/${userId}/transactions`).then(({ data }) => setTransactions(data || []));
    api.get(`/admin/users/${userId}/usage?days=30`).then(({ data }) => setUsage(data || { by_feature: [] }));
  }, [userId]);
  useEffect(refresh, [refresh]);

  return (
    <div className="space-y-4" data-testid="billing-panel">
      <div className="flex flex-wrap gap-2 items-center">
        <div className="flex-1 text-sm font-mono">
          Saldo actual: <span className="font-bold">{(kpis.wallet_balance_tokens || 0).toLocaleString()} tk</span>
        </div>
        <Button size="sm" className="rounded-sm" onClick={() => setGrantOpen(true)} data-testid="grant-bonus-button">
          <Coins className="h-3.5 w-3.5 mr-1.5" /> Acreditar bonus
        </Button>
      </div>

      <div className="grid md:grid-cols-3 gap-3 text-sm">
        <div className="border border-border bg-card p-3 rounded-sm">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Token ledger (últimas {ledger.length})</div>
          <ul className="text-xs font-mono space-y-1 max-h-72 overflow-y-auto">
            {ledger.slice(0, 50).map((l) => (
              <li key={l.id} className="flex justify-between gap-2">
                <span className={l.amount_tokens > 0 ? "text-emerald-600" : "text-red-600"}>
                  {l.amount_tokens > 0 ? "+" : ""}{l.amount_tokens}
                </span>
                <span className="text-muted-foreground truncate">{l.ref_kind}</span>
                <span className="text-muted-foreground">{l.created_at ? new Date(l.created_at).toLocaleDateString() : ""}</span>
              </li>
            ))}
            {ledger.length === 0 && <li className="text-muted-foreground">—</li>}
          </ul>
        </div>
        <div className="border border-border bg-card p-3 rounded-sm">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Transacciones Stripe</div>
          <ul className="text-xs font-mono space-y-1 max-h-72 overflow-y-auto">
            {transactions.slice(0, 50).map((t) => (
              <li key={t.id} className="flex justify-between gap-2">
                <span>${(t.amount_usd || 0).toFixed(2)}</span>
                <span className="text-muted-foreground truncate">{t.status}</span>
                <span className="text-muted-foreground">{t.created_at ? new Date(t.created_at).toLocaleDateString() : ""}</span>
              </li>
            ))}
            {transactions.length === 0 && <li className="text-muted-foreground">—</li>}
          </ul>
        </div>
        <div className="border border-border bg-card p-3 rounded-sm">
          <div className="text-xs uppercase tracking-wider font-mono text-muted-foreground mb-2">Uso (30d) por feature</div>
          <ul className="text-xs font-mono space-y-1 max-h-72 overflow-y-auto">
            {(usage.by_feature || []).map((row) => (
              <li key={row._id} className="flex justify-between gap-2">
                <span>{row._id}</span>
                <span className="text-muted-foreground">{row.events} ev</span>
                <span>${(row.sell_usd || 0).toFixed(2)}</span>
              </li>
            ))}
            {(usage.by_feature || []).length === 0 && <li className="text-muted-foreground">—</li>}
          </ul>
        </div>
      </div>

      {grantOpen && <GrantBonusDialog userId={userId} onClose={() => setGrantOpen(false)} onSaved={() => { setGrantOpen(false); refresh(); onChanged?.(); }} />}
    </div>
  );
}

function GrantBonusDialog({ userId, onClose, onSaved }) {
  const [tokens, setTokens] = useState(500);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!reason.trim() || reason.trim().length < 3) {
      return toast.error("Indicá un motivo (mín. 3 caracteres).");
    }
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/grant-bonus`, { tokens: Number(tokens), reason });
      toast.success(`Acreditados ${tokens} tokens`);
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Acreditar bonus</DialogTitle>
          <DialogDescription>El motivo queda registrado en el ledger y el audit log.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="number" value={tokens} onChange={(e) => setTokens(e.target.value)} placeholder="Cantidad" data-testid="grant-tokens" />
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motivo" data-testid="grant-reason" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="grant-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Acreditar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===========================================================================
// Tab: Danger zone
// ===========================================================================
function DangerZonePanel({ user, overview, onChanged }) {
  const [deactivateOpen, setDeactivateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const isActive = user.active !== false;
  const isSuperAdmin = user.role === "super_admin";

  // Phase 2 (B4) — embedding lock widget state
  const [embedLock, setEmbedLock] = useState(null); // {lock, file_count, ready_file_count} | null
  const [embedLoading, setEmbedLoading] = useState(true);
  const [reembedOpen, setReembedOpen] = useState(false);
  const [reembedRunning, setReembedRunning] = useState(false);

  const loadEmbedLock = useCallback(async () => {
    setEmbedLoading(true);
    try {
      const { data } = await api.get(`/admin/users/${user.id}/embedding-lock`);
      setEmbedLock(data);
    } catch {
      setEmbedLock(null);
    } finally {
      setEmbedLoading(false);
    }
  }, [user.id]);

  useEffect(() => { loadEmbedLock(); }, [loadEmbedLock]);

  const triggerReembed = async () => {
    setReembedRunning(true);
    try {
      const { data } = await api.post(`/admin/users/${user.id}/re-embed`);
      // Phase 2.5 — distinguish newly-flipped vs already-queued vs spawned
      const spawned = data.pipeline_tasks_spawned ?? 0;
      const requeued = data.files_requeued ?? 0;
      const totalQueued = data.total_queued_after ?? 0;
      const noSource = data.files_without_source ?? 0;
      if (spawned > 0) {
        toast.success(
          `Migration started · ${spawned} file(s) re-processing in background · refresh the file list in ~30s`,
        );
      } else if (requeued > 0) {
        toast.success(`${requeued} file(s) flipped to queued · no pipeline could run (missing source object)`);
      } else if (totalQueued > 0) {
        toast.message(`Already in progress · ${totalQueued} file(s) still queued from a prior migration`);
      } else {
        toast.success(`Re-embed complete · Qdrant wiped: ${data.qdrant_wiped ? "yes" : "no/already empty"} · no files to re-process`);
      }
      if (noSource > 0) {
        toast.warning(`${noSource} file(s) skipped — original upload object missing on record`);
      }
      setReembedOpen(false);
      await loadEmbedLock();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Re-embed failed");
    } finally {
      setReembedRunning(false);
    }
  };


  const exportData = async () => {
    try {
      const { data } = await api.get(`/admin/users/${user.id}/export`);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `faro-export-${user.email}-${Date.now()}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Export descargado");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Export failed");
    }
  };

  const forceLogout = async () => {
    try {
      await api.post(`/admin/users/${user.id}/force-logout`);
      toast.success("Sesiones revocadas");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    }
  };

  return (
    <div className="space-y-4" data-testid="danger-zone">
      {/* Phase 2 (B4) — Vector embedding lock + Re-embed user */}
      <div className="border border-border bg-card p-4 rounded-sm" data-testid="embedding-lock-widget">
        <div className="flex items-start gap-3">
          <Database className="h-5 w-5 text-sky-600 shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-bold text-sm">Vector embedding lock</div>
            <div className="text-xs text-muted-foreground mt-0.5">
              Pins this user to one embedding model so their Qdrant vectors keep a stable dimension. Admin can force a re-embed below.
            </div>
            <div className="mt-3 text-xs font-mono space-y-1">
              {embedLoading ? (
                <span className="text-muted-foreground"><Loader2 className="inline h-3 w-3 mr-1 animate-spin" /> Loading…</span>
              ) : embedLock?.lock ? (
                <>
                  <div data-testid="embedding-lock-model" className="flex items-center gap-2">
                    <span>
                      Locked to: <span className="text-foreground">{embedLock.lock.provider}/{embedLock.lock.model_id}</span>
                    </span>
                    {embedLock.is_stranded && (
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-sm text-[10px] uppercase tracking-wider font-bold border border-amber-400 bg-amber-50 dark:bg-amber-950/30 text-amber-800 dark:text-amber-200"
                        data-testid="embedding-stranded-chip"
                        title={embedLock.current_default ? `Admin default is ${embedLock.current_default.provider}/${embedLock.current_default.model_id}` : ""}
                      >
                        ⚠ Stranded
                      </span>
                    )}
                  </div>
                  <div data-testid="embedding-lock-when">
                    Since: <span className="text-muted-foreground">{embedLock.lock.locked_at ? new Date(embedLock.lock.locked_at).toLocaleString() : "—"}</span> ({embedLock.lock.source})
                  </div>
                  {embedLock.is_stranded && embedLock.current_default && (
                    <div className="text-amber-700 dark:text-amber-300" data-testid="embedding-current-default">
                      Current default: <span className="text-foreground">{embedLock.current_default.provider}/{embedLock.current_default.model_id}</span>
                    </div>
                  )}
                </>
              ) : (
                <div data-testid="embedding-lock-unlocked" className="text-muted-foreground">
                  Unlocked — first upload will claim a fresh lock automatically.
                </div>
              )}
              <div className="text-muted-foreground" data-testid="embedding-file-count">
                Files: {embedLock?.file_count ?? 0} total · {embedLock?.ready_file_count ?? 0} ready (embedded).
              </div>
            </div>
            <div className="mt-3">
              <Button
                size="sm"
                variant="outline"
                className="rounded-sm h-9 border-amber-300 text-amber-700 hover:bg-amber-50 dark:hover:bg-amber-950/30"
                onClick={() => setReembedOpen(true)}
                disabled={embedLoading || (!embedLock?.file_count && !embedLock?.lock)}
                data-testid="action-reembed-user"
                title={!embedLock?.lock ? "No lock and no files — nothing to migrate yet." : "Wipes Qdrant collection, clears lock, re-queues files."}
              >
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" />
                Re-embed user
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="border border-red-200 bg-red-50/30 p-4 rounded-sm">
        <div className="flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-red-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-bold text-red-700 text-sm">Acciones destructivas</div>
            <div className="text-xs text-red-600 mt-0.5">Todas las acciones de abajo quedan registradas en el audit log.</div>
          </div>
        </div>

        <div className="mt-4 grid sm:grid-cols-2 gap-3">
          <DangerAction
            icon={Power}
            title={isActive ? "Desactivar cuenta" : "Reactivar cuenta"}
            desc={isActive ? "Bloquea login y revoca sesiones. Saldo preservado." : "Restaura acceso normal."}
            disabled={isSuperAdmin}
            disabledHint={isSuperAdmin ? "No se puede cambiar el estado de un super_admin." : null}
            onClick={() => setDeactivateOpen(true)}
            testid="action-deactivate"
            variant={isActive ? "danger" : "primary"}
            label={isActive ? "Desactivar" : "Reactivar"}
          />
          <DangerAction
            icon={LogOut}
            title="Forzar cierre de sesión"
            desc="Invalida todos los tokens en uso. El usuario debe volver a loguear."
            onClick={forceLogout}
            testid="action-force-logout"
            variant="warn"
            label="Forzar logout"
          />
          <DangerAction
            icon={KeyRound}
            title="Resetear contraseña"
            desc="Asigna una contraseña temporal. Forzá cambio al próximo login."
            onClick={() => setResetOpen(true)}
            testid="action-reset-pass"
            variant="warn"
            label="Resetear"
          />
          <DangerAction
            icon={Download}
            title="Exportar datos (GDPR)"
            desc="JSON completo con perfil, chats, archivos, billing, sesiones."
            onClick={exportData}
            testid="action-export"
            variant="primary"
            label="Exportar"
          />
          <DangerAction
            icon={Trash2}
            title="Eliminar cuenta"
            desc="Borra todo el usuario y sus datos (cascade). Irreversible."
            disabled={isSuperAdmin}
            disabledHint={isSuperAdmin ? "No se puede eliminar un super_admin." : null}
            onClick={() => setDeleteOpen(true)}
            testid="action-delete"
            variant="danger"
            label="Eliminar"
          />
        </div>
      </div>

      {deactivateOpen && (
        <StatusChangeDialog
          user={user}
          newActive={!isActive}
          onClose={() => setDeactivateOpen(false)}
          onSaved={() => { setDeactivateOpen(false); onChanged?.(); }}
        />
      )}
      {reembedOpen && (
        <Dialog open onOpenChange={(o) => !o && !reembedRunning && setReembedOpen(false)}>
          <DialogContent data-testid="reembed-confirm-dialog">
            <DialogHeader>
              <DialogTitle>Re-embed this user?</DialogTitle>
              <DialogDescription asChild>
                <div className="space-y-3 text-sm">
                  <div>
                    This will:
                    <ul className="mt-2 ml-5 list-disc text-xs text-muted-foreground space-y-1">
                      <li>Delete the user's Qdrant collection (<code className="font-mono">client_{user.id}_chunks</code>).</li>
                      <li>Clear their embedding lock so the next upload picks up the current admin default.</li>
                      <li>Re-queue all <strong>{embedLock?.file_count ?? 0}</strong> file(s) for re-embedding.</li>
                    </ul>
                  </div>
                  <div className="text-xs border border-amber-400/40 bg-amber-500/10 rounded-sm p-2">
                    The user will see <em>"Re-embedding queued"</em> as the status until the pipeline runs (or until they re-upload). Search results may be empty during this window.
                  </div>
                </div>
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReembedOpen(false)} disabled={reembedRunning} data-testid="reembed-cancel">Cancel</Button>
              <Button onClick={triggerReembed} disabled={reembedRunning} data-testid="reembed-confirm">
                {reembedRunning ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Re-embedding…</> : "Re-embed user"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      {resetOpen && (
        <ResetPasswordDialog
          userId={user.id}
          onClose={() => setResetOpen(false)}
          onSaved={() => { setResetOpen(false); onChanged?.(); }}
        />
      )}
      {deleteOpen && (
        <DeleteUserDialog
          user={user}
          onClose={() => setDeleteOpen(false)}
        />
      )}
    </div>
  );
}

function DangerAction({ icon: Icon, title, desc, onClick, label, variant = "danger", disabled = false, disabledHint, testid }) {
  return (
    <div className={`border bg-card p-3 rounded-sm ${disabled ? "opacity-50" : ""}`}>
      <div className="flex items-start gap-2">
        <Icon className="h-4 w-4 mt-0.5 shrink-0 text-muted-foreground" />
        <div className="flex-1">
          <div className="text-sm font-semibold">{title}</div>
          <div className="text-xs text-muted-foreground mt-0.5">{desc}</div>
        </div>
      </div>
      <Button
        size="sm"
        variant={variant === "danger" ? "destructive" : "outline"}
        className="rounded-sm w-full mt-2"
        onClick={onClick}
        disabled={disabled}
        title={disabledHint || ""}
        data-testid={testid}
      >
        {label}
      </Button>
    </div>
  );
}

function StatusChangeDialog({ user, newActive, onClose, onSaved }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!newActive && reason.trim().length < 3) {
      return toast.error("Indicá un motivo (mín. 3 caracteres).");
    }
    setBusy(true);
    try {
      await api.patch(`/admin/users/${user.id}/status`, { active: newActive, reason });
      toast.success(newActive ? "Cuenta reactivada" : "Cuenta desactivada");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>{newActive ? "Reactivar cuenta" : "Desactivar cuenta"}</DialogTitle>
          <DialogDescription>
            {newActive ? "El usuario podrá loguear nuevamente." : `Bloquea login y revoca sesiones de ${user.email}. El saldo NO se borra.`}
          </DialogDescription>
        </DialogHeader>
        <Textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          rows={2}
          placeholder={newActive ? "Motivo de reactivación (opcional)" : "Motivo (obligatorio)"}
          data-testid="status-reason"
        />
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant={newActive ? "default" : "destructive"} onClick={submit} disabled={busy} data-testid="status-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            {newActive ? "Reactivar" : "Desactivar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ResetPasswordDialog({ userId, onClose, onSaved }) {
  const [pw, setPw] = useState("");
  const [force, setForce] = useState(true);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (pw.length < 6) return toast.error("Mín. 6 caracteres.");
    setBusy(true);
    try {
      await api.post(`/admin/users/${userId}/reset-password`, { new_password: pw, force_change_on_login: force });
      toast.success("Contraseña reseteada · sesiones revocadas");
      onSaved?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle>Resetear contraseña</DialogTitle>
          <DialogDescription>Comunica esta contraseña al usuario de forma segura — no se envía por email.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <Input type="text" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="Nueva contraseña temporal" data-testid="reset-pass-input" />
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} className="h-4 w-4" />
            Forzar cambio en el próximo login
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={submit} disabled={busy} data-testid="reset-pass-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Resetear
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DeleteUserDialog({ user, onClose }) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();
  const canDelete = typed === user.email;
  const submit = async () => {
    if (!canDelete) return;
    setBusy(true);
    try {
      const { data } = await api.delete(`/admin/users/${user.id}?confirm_email=${encodeURIComponent(user.email)}`);
      toast.success(`Eliminado · ${Object.values(data.deleted || {}).reduce((a, b) => a + b, 0)} filas`);
      navigate("/admin");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm">
        <DialogHeader>
          <DialogTitle className="text-red-600">Eliminar cuenta</DialogTitle>
          <DialogDescription>
            Esto borra al usuario y todos sus chats, archivos, grabaciones, ledger, transacciones y sesiones. <strong>No tiene reverso.</strong>
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Para confirmar, escribí <strong className="font-mono">{user.email}</strong> abajo:
          </div>
          <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder={user.email} data-testid="delete-confirm-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={submit} disabled={!canDelete || busy} data-testid="delete-submit">
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
            Eliminar definitivamente
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Empty({ msg }) {
  return <div className="border border-border bg-card p-10 rounded-sm text-center text-sm text-muted-foreground">{msg}</div>;
}
